"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const material_1 = require("@mui/material");
const icons_material_1 = require("@mui/icons-material");
const templateLibraryApi_1 = require("../api/templateLibraryApi");
const sessionBuilder_1 = require("../api/sessionBuilder");
const thumbnailGenerator_1 = require("../utils/thumbnailGenerator");
const SaveAsTemplateModal = ({ open, onClose, htmlContent, docType = sessionBuilder_1.DOC_TYPE.PPT, defaultName = '', }) => {
    const [templateName, setTemplateName] = (0, react_1.useState)('');
    const [templateDescription, setTemplateDescription] = (0, react_1.useState)('');
    const [accessLevel, setAccessLevel] = (0, react_1.useState)(templateLibraryApi_1.TemplateAccessLevel.PERSONAL);
    const [thumbnailUrl, setThumbnailUrl] = (0, react_1.useState)(null);
    const [isSaving, setIsSaving] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const [success, setSuccess] = (0, react_1.useState)(false);
    // Reset form when modal opens
    (0, react_1.useEffect)(() => {
        if (open) {
            setTemplateName(defaultName || '');
            setTemplateDescription('');
            setAccessLevel(templateLibraryApi_1.TemplateAccessLevel.PERSONAL);
            setThumbnailUrl(null);
            setError(null);
            setSuccess(false);
            // Auto-generate description from content if not provided
            if (!defaultName && htmlContent) {
                const previewText = (0, thumbnailGenerator_1.extractPreviewText)(htmlContent, 150);
                setTemplateDescription(`Template created from: ${previewText}`);
            }
        }
    }, [open, defaultName, htmlContent]);
    // Generate thumbnail when HTML content changes
    (0, react_1.useEffect)(() => {
        if (open && htmlContent) {
            generateThumbnail();
        }
    }, [open, htmlContent]);
    // Test thumbnail generation on component mount
    (0, react_1.useEffect)(() => {
        if (open && !htmlContent) {
            console.log('No HTML content available, creating test thumbnail');
            // Create a test thumbnail to verify the generation works
            const testHtml = '<div class="presentation-slide"><h1>Test Template</h1><p>This is a test slide for thumbnail generation.</p></div>';
            (0, thumbnailGenerator_1.generateThumbnailFromHtml)(testHtml, 200, 113).then((dataUrl) => {
                console.log('Test thumbnail generated, length:', dataUrl.length);
                console.log('Test thumbnail preview:', dataUrl.substring(0, 100) + '...');
                if (dataUrl && dataUrl.startsWith('data:image/')) {
                    setThumbnailUrl(dataUrl);
                }
                else {
                    console.error('Invalid test thumbnail generated');
                }
            }).catch((err) => {
                console.error('Test thumbnail generation failed:', err);
            });
        }
    }, [open]);
    const generateThumbnail = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!htmlContent) {
            console.log('No HTML content available for thumbnail generation');
            return;
        }
        console.log('Generating thumbnail for HTML content:', htmlContent.substring(0, 100) + '...');
        try {
            // Generate exact thumbnail capture
            const thumbnailDataUrl = yield (0, thumbnailGenerator_1.generateThumbnailFromHtml)(htmlContent, 200, 113);
            console.log('Exact thumbnail captured, length:', thumbnailDataUrl.length);
            console.log('Thumbnail data URL preview:', thumbnailDataUrl.substring(0, 100) + '...');
            if (thumbnailDataUrl && thumbnailDataUrl.startsWith('data:image/')) {
                setThumbnailUrl(thumbnailDataUrl);
                console.log('Exact thumbnail URL set successfully');
            }
            else {
                console.error('Invalid thumbnail data URL generated');
                setThumbnailUrl(null);
            }
        }
        catch (err) {
            console.error('Failed to generate exact thumbnail:', err);
            // Set a fallback thumbnail
            setThumbnailUrl(null);
        }
    });
    const handleSaveTemplate = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!templateName.trim()) {
            setError('Template name is required');
            return;
        }
        if (!htmlContent.trim()) {
            setError('No content to save as template');
            return;
        }
        setIsSaving(true);
        setError(null);
        try {
            const templateData = {
                name: templateName.trim(),
                description: templateDescription.trim() || undefined,
                docType,
                accessLevel,
                html: htmlContent,
                thumbnailUrl: thumbnailUrl || undefined,
                tags: [], // Could be enhanced to allow user to add tags
            };
            yield templateLibraryApi_1.templateLibraryApi.createTemplate(templateData);
            setSuccess(true);
            // Close modal after a brief delay
            setTimeout(() => {
                onClose();
            }, 1500);
        }
        catch (err) {
            console.error('Failed to save template:', err);
            setError(err.message || 'Failed to save template. Please try again.');
        }
        finally {
            setIsSaving(false);
        }
    });
    const handleClose = () => {
        if (!isSaving) {
            onClose();
        }
    };
    return ((0, jsx_runtime_1.jsxs)(material_1.Dialog, { open: open, onClose: handleClose, maxWidth: "sm", fullWidth: true, PaperProps: {
            sx: { minHeight: '500px' }
        }, children: [(0, jsx_runtime_1.jsx)(material_1.DialogTitle, { children: (0, jsx_runtime_1.jsxs)(material_1.Box, { display: "flex", justifyContent: "space-between", alignItems: "center", children: [(0, jsx_runtime_1.jsx)(material_1.Typography, { variant: "h6", children: "Save as Template" }), (0, jsx_runtime_1.jsx)(material_1.IconButton, { onClick: handleClose, size: "small", disabled: isSaving, children: (0, jsx_runtime_1.jsx)(icons_material_1.Close, {}) })] }) }), (0, jsx_runtime_1.jsx)(material_1.DialogContent, { dividers: true, children: (0, jsx_runtime_1.jsxs)(material_1.Box, { display: "flex", flexDirection: "column", gap: 3, children: [(0, jsx_runtime_1.jsxs)(material_1.Box, { children: [(0, jsx_runtime_1.jsx)(material_1.Typography, { variant: "subtitle2", gutterBottom: true, children: "Template Preview" }), (0, jsx_runtime_1.jsx)(material_1.Box, { sx: {
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        minHeight: '169px', // Minimum height
                                        maxHeight: '400px', // Maximum height to prevent huge images
                                        width: '100%',
                                        position: 'relative',
                                        backgroundColor: '#f8f9fa',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto',
                                    }, children: thumbnailUrl ? ((0, jsx_runtime_1.jsx)("img", { src: thumbnailUrl, alt: "Template Preview", style: {
                                            maxWidth: '100%',
                                            maxHeight: '400px', // Limit maximum height
                                            width: 'auto',
                                            height: 'auto',
                                            objectFit: 'contain',
                                            display: 'block',
                                        } })) : ((0, jsx_runtime_1.jsxs)(material_1.Box, { textAlign: "center", color: "text.secondary", children: [(0, jsx_runtime_1.jsx)(material_1.Typography, { variant: "body2", children: "Generating preview..." }), (0, jsx_runtime_1.jsx)(material_1.CircularProgress, { size: 24, sx: { mt: 1 } })] })) })] }), (0, jsx_runtime_1.jsx)(material_1.TextField, { fullWidth: true, label: "Template Name", value: templateName, onChange: (e) => setTemplateName(e.target.value), placeholder: "Enter template name...", required: true, error: !templateName.trim() && templateName !== '', helperText: !templateName.trim() && templateName !== '' ? "Template name is required" : "", disabled: isSaving }), (0, jsx_runtime_1.jsx)(material_1.TextField, { fullWidth: true, label: "Description (Optional)", value: templateDescription, onChange: (e) => setTemplateDescription(e.target.value), placeholder: "Enter template description...", multiline: true, rows: 2, disabled: isSaving, helperText: "This description will help others understand what this template contains." }), (0, jsx_runtime_1.jsxs)(material_1.Box, { children: [(0, jsx_runtime_1.jsx)(material_1.Typography, { variant: "subtitle2", gutterBottom: true, children: "Access Level" }), (0, jsx_runtime_1.jsx)(material_1.Box, { display: "flex", gap: 1, children: Object.values(templateLibraryApi_1.TemplateAccessLevel).map((level) => ((0, jsx_runtime_1.jsxs)(material_1.Button, { variant: accessLevel === level ? 'contained' : 'outlined', size: "small", onClick: () => setAccessLevel(level), disabled: isSaving, sx: {
                                            textTransform: 'capitalize',
                                            flex: 1,
                                        }, children: [level === templateLibraryApi_1.TemplateAccessLevel.PUBLIC && 'Public', level === templateLibraryApi_1.TemplateAccessLevel.ORGANIZATION && 'Organization', level === templateLibraryApi_1.TemplateAccessLevel.PERSONAL && 'Personal'] }, level))) })] }), error && ((0, jsx_runtime_1.jsx)(material_1.Alert, { severity: "error", children: error })), success && ((0, jsx_runtime_1.jsx)(material_1.Alert, { severity: "success", children: "Template saved successfully!" }))] }) }), (0, jsx_runtime_1.jsxs)(material_1.DialogActions, { sx: { p: 3 }, children: [(0, jsx_runtime_1.jsx)(material_1.Button, { onClick: handleClose, color: "primary", disabled: isSaving, children: "Cancel" }), (0, jsx_runtime_1.jsx)(material_1.Button, { onClick: handleSaveTemplate, variant: "contained", color: "primary", disabled: !templateName.trim() || isSaving, startIcon: isSaving ? (0, jsx_runtime_1.jsx)(material_1.CircularProgress, { size: 16 }) : (0, jsx_runtime_1.jsx)(icons_material_1.Save, {}), children: isSaving ? 'Saving...' : 'Save Template' })] })] }));
};
exports.default = SaveAsTemplateModal;
